import React, { useState, useEffect, useRef } from 'react';
import { NavLink, Link, useNavigate } from 'react-router-dom';
import SearchBar from './SearchBar';
import { useAuth } from '../context/AuthContext';

interface HeaderProps {
  onMenuClick?: () => void;
  title?: string;
}

export default function Header({
  onMenuClick,
  title = 'Food Safety Digest',
}: HeaderProps) {
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const { isAuthenticated, username, logout } = useAuth();
  const navigate = useNavigate();

  // Close mobile menu on outside click
  useEffect(() => {
    if (!menuOpen) return;
    function handleClick(e: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setMenuOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [menuOpen]);

  const publicItems = [
    { to: '/', label: 'Dashboard', end: true },
    { to: '/news', label: 'News' },
    { to: '/legislation/pipeline', label: 'Upcoming Law' },
    { to: '/qms', label: 'QMS' },
    { to: '/subscribe', label: 'Subscribe' },
  ];

  const protectedItems = [
    { to: '/knowledge', label: 'Knowledge Center' },
    { to: '/admin', label: 'Manage' },
  ];

  const navItems = isAuthenticated
    ? [...publicItems, ...protectedItems]
    : publicItems;

  function handleLogout() {
    logout();
    navigate('/');
  }

  return (
    <header className="header" ref={menuRef}>
      <Link to="/" className="header-brand">
        <div className="header-logo">FS</div>
        <span className="header-title">{title}</span>
      </Link>

      {isAuthenticated && <SearchBar />}

      {/* Desktop nav */}
      <nav className="header-nav">
        {navItems.map(item => (
          <NavLink
            key={item.to}
            to={item.to}
            end={item.end}
            className={({ isActive }) => `nav-link${isActive ? ' active' : ''}`}
          >
            {item.label}
          </NavLink>
        ))}
        {isAuthenticated ? (
          <button className="nav-link auth-btn" onClick={handleLogout} title={`Logged in as ${username}`}>
            Logout
          </button>
        ) : (
          <NavLink to="/login" className={({ isActive }) => `nav-link${isActive ? ' active' : ''}`}>
            Login
          </NavLink>
        )}
      </nav>

      {/* Mobile hamburger */}
      <button
        className="header-menu-btn"
        onClick={() => {
          setMenuOpen(o => !o);
          onMenuClick?.();
        }}
        aria-label="Toggle menu"
      >
        {menuOpen ? '\u2715' : '\u2630'}
      </button>

      {menuOpen && (
        <nav className="header-nav-mobile">
          {navItems.map(item => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              className={({ isActive }) => `nav-link${isActive ? ' active' : ''}`}
              onClick={() => setMenuOpen(false)}
            >
              {item.label}
            </NavLink>
          ))}
          {isAuthenticated ? (
            <button className="nav-link auth-btn" onClick={() => { handleLogout(); setMenuOpen(false); }}>
              Logout
            </button>
          ) : (
            <NavLink to="/login" className="nav-link" onClick={() => setMenuOpen(false)}>
              Login
            </NavLink>
          )}
        </nav>
      )}
    </header>
  );
}
